// UPost - About Us Page
// Clearly describes the legitimate business for transparency
import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Building, Users, Shield, Award, BookOpen, 
  Mail, Phone, MapPin, CheckCircle
} from 'lucide-react';

export function About() {
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Hero */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            {/* Verified Badge */}
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Verified Business - Web-Tech Services</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About UPost
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              AI-powered writing tools for authors, screenwriters, and content creators
            </p>
            <p className="text-lg text-purple-600 font-medium">
              A product of Web-Tech Services
            </p>
          </div>
        </section>

        {/* Company Info */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Building className="w-6 h-6 text-purple-600" />
                  Our Company
                </h2>
                <p className="text-gray-600 mb-4">
                  UPost is a <strong>software-as-a-service (SaaS) platform</strong> owned and 
                  operated by <strong>Web-Tech Services</strong>, a verified business based in 
                  Arlington, Texas. We develop AI-powered writing tools that help authors 
                  and screenwriters create, edit, and publish their work.
                </p>
                <p className="text-gray-600 mb-4">
                  Founded with the mission to democratize professional writing tools, 
                  UPost combines cutting-edge AI technology with industry-standard 
                  formatting to help writers at every stage of their journey.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Parent Company:</strong> Web-Tech Services<br />
                    <strong>Product:</strong> UPost (upost.com)<br />
                    <strong>Business Type:</strong> Online Software Service (SaaS)<br />
                    <strong>Industry:</strong> Software / Publishing Technology<br />
                    <strong>Headquarters:</strong> Arlington, TX, USA
                  </p>
                </div>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-purple-600" />
                  What We Do
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span><strong>AI Writing Assistant:</strong> Help authors write novels, screenplays, and content faster</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span><strong>Document Editor:</strong> Professional writing environment with auto-save and formatting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span><strong>Format Conversion:</strong> Convert novels to industry-standard screenplay format</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span><strong>Publishing Guides:</strong> Educational resources for self-publishing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span><strong>Copyright Assistance:</strong> Help with U.S. Copyright Office registration</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Important Disclaimers */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 text-amber-800">Important Information</h3>
                <ul className="space-y-2 text-amber-900">
                  <li>• UPost is an <strong>online software service</strong> - we do not have a physical storefront for customers to visit</li>
                  <li>• We are <strong>not affiliated</strong> with Amazon, Netflix, or any studios mentioned in our educational content</li>
                  <li>• Our AI tools are <strong>writing assistants</strong> - all content should be reviewed and edited by the user</li>
                  <li>• We do not guarantee publishing success, bestseller status, or studio deals</li>
                  <li>• Copyright registration assistance is administrative - we are not a law firm</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-2xl font-bold mb-6 text-center">Contact Us</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <Card>
                <CardContent className="p-6">
                  <Mail className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Email</h3>
                  <a href="mailto:lscala@upost.com" className="text-purple-600 hover:underline">
                    lscala@upost.com
                  </a>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Phone className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Phone</h3>
                  <a href="tel:+18173205334" className="text-purple-600 hover:underline">
                    (817) 320-5334
                  </a>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <MapPin className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Location</h3>
                  <p className="text-gray-600">Arlington, TX<br />United States</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-2xl font-bold mb-8 text-center">Why Trust UPost?</h2>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div className="p-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold mb-1">Verified Business</h3>
                <p className="text-sm text-gray-600">Google Verified via Web-Tech Services</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-1">Secure Platform</h3>
                <p className="text-sm text-gray-600">SSL encrypted, data protected</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-1">US-Based Company</h3>
                <p className="text-sm text-gray-600">Arlington, Texas</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Award className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold mb-1">Real Support</h3>
                <p className="text-sm text-gray-600">Phone & email support</p>
              </div>
            </div>
            
            {/* Parent Company Card */}
            <div className="mt-8">
              <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-purple-900 mb-2">Parent Company</h3>
                  <a 
                    href="https://web-techservices.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-lg text-purple-700 hover:text-purple-900 font-semibold"
                  >
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Web-Tech Services
                  </a>
                  <p className="text-purple-600 mt-1">Google Verified Business • Arlington, TX</p>
                  <p className="text-sm text-gray-600 mt-2">
                    UPost is proudly developed and maintained by Web-Tech Services, 
                    a verified technology company serving clients since establishment.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Start Writing?</h2>
            <p className="text-gray-600 mb-6">
              Join thousands of authors using UPost to bring their stories to life.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/register">
                <Button size="lg">Get Started Free</Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline">View Pricing</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default About;
